import {
  createIssue,
  listIssues,
  resolveUserUrnId,
  updateIssue,
} from "../../../services/issuesApi";
import pushImg from "./img/pushPin6.svg";

// Converts inline SVG into a data-URI usable in CSS background-image
const svgToDataUri = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

// White monochrome icons matching the native Autodesk toolbar look
const ICONS = {
  safety: svgToDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path d="M12 2.5 19 5.5V11c0 5.2-3.2 9-7 10.5C8.2 20 5 16.2 5 11V5.5L12 2.5Z" fill="#fff"/><path d="M9 12l2 2 4-4" stroke="#111" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  ),
  design: svgToDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path d="M3 15 15 3l6 6L9 21H3v-6Z" fill="#fff"/><path d="M11.5 6.5l6 6" stroke="#111" stroke-width="1.4"/></svg>`,
  ),
  rfi: svgToDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="4" fill="#fff"/><path d="M9.2 9.5a2.8 2.8 0 1 1 4.4 2.3c-.9.6-1.4 1.1-1.4 2.1" stroke="#111" stroke-width="1.6" stroke-linecap="round"/><circle cx="12" cy="17" r="1" fill="#111"/></svg>`,
  ),
  showIssues: svgToDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" fill="#fff"/><circle cx="12" cy="12" r="2.6" fill="#111"/><path d="M3 3l18 18" stroke="#111" stroke-width="1.8" stroke-linecap="round"/></svg>`,
  ),
  showRfis: svgToDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="4" fill="#fff"/><path d="M12 8v8M8 12h8" stroke="#111" stroke-width="1.8" stroke-linecap="round"/></svg>`,
  ),
};
export const PushpinVEContainer = (Autodesk) => {
  const EXT = {
    PUSH_PIN: "Autodesk.BIM360.Extension.PushPin",
    BIM360_ISSUE: "BIM360IssueExtension",
  };

  const TB = {
    MAIN: "MyAppToolbar",
    CREATE_QUALITY: "createQualityIssue",
    CREATE_SAFETY: "createSafetyIssue",
    CREATE_DESIGN: "createDesignIssue",
    CREATE_RFI: "createRFI",
    SHOW_ISSUES: "showAllIssues",
    SHOW_RFIS: "showAllRFIs",
  };

  const STATUS_COLORS = {
    draft: "#9E9E9E",
    open: "#FF9800",
    pending: "#03A9F4",
    in_progress: "#8BC34A",
    completed: "#4CAF50",
    in_review: "#9C27B0",
    not_approved: "#F44336",
    in_dispute: "#E91E63",
  };

  const ISSUE_TYPE_CONFIGS = {
    QUALITY: {
      type: "issues",
      subtype: "quality_issue",
      defaultLabel: "Quality Issue",
      title: "Quality Issue",
      tooltip: "Create Quality Issue",
      className: "createQualityIssue",
      icon: "🛠️",
      color: "#FF9800",
      pinColor: "#FF9800",
    },
    SAFETY: {
      type: "issues",
      subtype: "safety_issue",
      defaultLabel: "Safety Issue",
      title: "Safety Issue",
      tooltip: "Create Safety Issue",
      className: "createSafetyIssue",
      icon: "🛡️",
      color: "#F44336",
      pinColor: "#F44336",
    },
    DESIGN: {
      type: "issues",
      subtype: "design_issue",
      defaultLabel: "Design Issue",
      title: "Design Issue",
      tooltip: "Create Design Issue",
      className: "createDesignIssue",
      icon: "📐",
      color: "#2196F3",
      pinColor: "#2196F3",
    },
    RFI: {
      type: "rfis",
      subtype: "rfi",
      defaultLabel: "RFI",
      title: "RFI",
      tooltip: "Create RFI",
      className: "createRFI",
      icon: "❓",
      color: "#4CAF50",
      pinColor: "#4CAF50",
    },
  };

  class BIM360IssueExtension extends Autodesk.Viewing.Extension {
    constructor(viewer, options) {
      super(viewer, options);
      this.subToolbar = null;
      this.buttons = {};
      this._toolbarCreated = false; // guard: build UI only once
      this.isCreatingIssue = false;
      this.pushPinExtension = null;
      this.allIssues = [];
      this.allRFIs = [];
      this.currentIssueType = null;
      this._pendingFormData = null;
      this.pushpinColorMap = new Map();
      this.pushpinObserver = null;
      this.colorUpdateInterval = null;
      this.backupInterval = null;
      this._toolbarPollTimer = null;
      this._syncTimer = null;
      /** True while programmatically calling createItem to restore pins from API (avoids duplicate POST). */
      this._restoringPins = false;

      this.onPushpinCreated = this.onPushpinCreated.bind(this);
      this.onPushpinLoaded = this.onPushpinLoaded.bind(this);
      this.onGeometryLoaded = this.onGeometryLoaded.bind(this);
      this.applyColorToPushpin = this.applyColorToPushpin.bind(this);
      this.continuouslyApplyColors = this.continuouslyApplyColors.bind(this);
      this.handleCreateClick = this.handleCreateClick.bind(this);
    }

    // ─── Lifecycle ─────────────────────────────────────────────────────────────

    load() {
      console.log("[BIM360] Extension loading…");
      this.injectToolbarStyles();
      this.startColorUpdates();
      void this._initIssuesHydration();
      this.startAutoBackup();
      this.registerBridge();

      // ── Toolbar: try immediately, then poll ──────────────────────────────────
      // viewer.toolbar may already exist (e.g. GuiViewer3D fully initialised)
      if (this.viewer.toolbar) {
        this._buildToolbar();
      } else {
        // Poll every 200 ms until toolbar appears (max 15 s)
        let attempts = 0;
        this._toolbarPollTimer = setInterval(() => {
          attempts++;
          if (this.viewer.toolbar) {
            clearInterval(this._toolbarPollTimer);
            this._toolbarPollTimer = null;
            this._buildToolbar();
          } else if (attempts > 75) {
            // 75 × 200 ms = 15 s timeout
            clearInterval(this._toolbarPollTimer);
            this._toolbarPollTimer = null;
            console.warn(
              "[BIM360] Toolbar not found after 15 s — buttons not added.",
            );
          }
        }, 200);
      }

      console.log("[BIM360] Loaded");
      return true;
    }

    unload() {
      if (this._toolbarPollTimer) {
        clearInterval(this._toolbarPollTimer);
        this._toolbarPollTimer = null;
      }
      this.stopColorUpdates();
      this.stopAutoBackup();
      this.removeViewerListeners();
      this.unregisterBridge();
      this.pushpinObserver?.disconnect();
      this.pushpinObserver = null;
      if (this.subToolbar && this.viewer.toolbar) {
        this.viewer.toolbar.removeControl(this.subToolbar);
        this.subToolbar = null;
      }
      this.removeStyles();
      if (this.pushPinExtension) {
        this.viewer.unloadExtension(EXT.PUSH_PIN);
        this.pushPinExtension = null;
      }
      console.log("[BIM360] Unloaded");
      return true;
    }

    // onToolbarCreated is the official callback — keep as backup
    onToolbarCreated() {
      this._buildToolbar();
    }

    // ─── Build toolbar (guarded — runs only once) ──────────────────────────────

    // _buildToolbar() {
    //   if (this._toolbarCreated) return;
    //   if (!this.viewer.toolbar) {
    //     console.warn(
    //       "[BIM360] _buildToolbar called but viewer.toolbar is null",
    //     );
    //     return;
    //   }
    //   this._toolbarCreated = true;
    //   console.log("[BIM360] Building toolbar buttons…");

    //   // Re-use existing group or create new one
    //   this.subToolbar = this.viewer.toolbar.getControl(TB.MAIN);
    //   if (!this.subToolbar) {
    //     this.subToolbar = new Autodesk.Viewing.UI.ControlGroup(TB.MAIN);
    //     this.viewer.toolbar.addControl(this.subToolbar);
    //   }

    //   this._addBtn(TB.CREATE_QUALITY, ISSUE_TYPE_CONFIGS.QUALITY);
    //   this._addBtn(TB.CREATE_SAFETY, ISSUE_TYPE_CONFIGS.SAFETY);
    //   this._addBtn(TB.CREATE_DESIGN, ISSUE_TYPE_CONFIGS.DESIGN);
    //   this._addBtn(TB.CREATE_RFI, ISSUE_TYPE_CONFIGS.RFI);
    //   this._addSep();
    //   this._addShowBtn(
    //     TB.SHOW_ISSUES,
    //     "showAllIssues",
    //     "Show All Issues",
    //     "issues",
    //   );
    //   this._addShowBtn(TB.SHOW_RFIS, "showAllRFIs", "Show All RFIs", "rfis");

    //   console.log("[BIM360] ✅ Toolbar buttons added");
    // }

    _buildToolbar() {
      if (this._toolbarCreated) return;
      if (!this.viewer.toolbar) {
        console.warn(
          "[BIM360] _buildToolbar called but viewer.toolbar is null",
        );
        return;
      }
      this._toolbarCreated = true;
      console.log("[BIM360] Building toolbar buttons…");

      this.subToolbar = this.viewer.toolbar.getControl(TB.MAIN);
      if (!this.subToolbar) {
        this.subToolbar = new Autodesk.Viewing.UI.ControlGroup(TB.MAIN);
        this.viewer.toolbar.addControl(this.subToolbar);
      }

      this._addCreateIssueCombo();
      this._addSep();
      // this._addShowCombo();

      console.log("[BIM360] ✅ Toolbar buttons added");
    }

    // ─── Flyout: Create Quality / Safety / Design / RFI ─────────────────────────
    _addCreateIssueCombo() {
      const comboId = "createIssueCombo";
      if (this.subToolbar.getControl(comboId)) {
        this.subToolbar.removeControl(comboId);
      }

      const combo = new Autodesk.Viewing.UI.ComboButton(comboId);
      combo.setToolTip("Create Issue");
      combo.addClass("createIssueCombo");

      const items = [
        { id: TB.CREATE_QUALITY, cfg: ISSUE_TYPE_CONFIGS.QUALITY },
        { id: TB.CREATE_SAFETY, cfg: ISSUE_TYPE_CONFIGS.SAFETY },
        { id: TB.CREATE_DESIGN, cfg: ISSUE_TYPE_CONFIGS.DESIGN },
        { id: TB.CREATE_RFI, cfg: ISSUE_TYPE_CONFIGS.RFI },
      ];

      items.forEach(({ id, cfg }) => {
        const btn = new Autodesk.Viewing.UI.Button(id);
        btn.onClick = () => this.handleCreateClick(cfg);
        btn.addClass(cfg.className);
        btn.setToolTip(cfg.tooltip);
        combo.addControl(btn);
        this.buttons[id] = btn;
      });

      this.subToolbar.addControl(combo);
      this.buttons[comboId] = combo;
    }

    // ─── Flyout: Show All Issues / Show All RFIs ────────────────────────────────
    // _addShowCombo() {
    //   const comboId = "showCombo";
    //   if (this.subToolbar.getControl(comboId)) {
    //     this.subToolbar.removeControl(comboId);
    //   }

    //   const combo = new Autodesk.Viewing.UI.ComboButton(comboId);
    //   combo.setToolTip("Show");
    //   combo.addClass("showCombo");

    //   const showIssuesBtn = new Autodesk.Viewing.UI.Button(TB.SHOW_ISSUES);
    //   showIssuesBtn.onClick = async () => this.showAllPushPins("issues");
    //   showIssuesBtn.addClass("showAllIssues");
    //   showIssuesBtn.setToolTip("Show All Issues");
    //   combo.addControl(showIssuesBtn);
    //   this.buttons[TB.SHOW_ISSUES] = showIssuesBtn;

    //   const showRfisBtn = new Autodesk.Viewing.UI.Button(TB.SHOW_RFIS);
    //   showRfisBtn.onClick = async () => this.showAllPushPins("rfis");
    //   showRfisBtn.addClass("showAllRFIs");
    //   showRfisBtn.setToolTip("Show All RFIs");
    //   combo.addControl(showRfisBtn);
    //   this.buttons[TB.SHOW_RFIS] = showRfisBtn;

    //   this.subToolbar.addControl(combo);
    //   this.buttons[comboId] = combo;
    // }

    _addBtn(id, cfg) {
      // Remove stale button if already registered (e.g. hot-reload)
      if (this.subToolbar.getControl(id)) {
        this.subToolbar.removeControl(id);
      }
      const btn = new Autodesk.Viewing.UI.Button(id);
      btn.onClick = () => this.handleCreateClick(cfg);
      btn.addClass(cfg.className);
      btn.setToolTip(cfg.tooltip);
      this.subToolbar.addControl(btn);
      this.buttons[id] = btn;
    }

    _addShowBtn(id, cls, tip, type) {
      if (this.subToolbar.getControl(id)) {
        this.subToolbar.removeControl(id);
      }
      const btn = new Autodesk.Viewing.UI.Button(id);
      btn.onClick = async () => this.showAllPushPins(type);
      btn.addClass(cls);
      btn.setToolTip(tip);
      this.subToolbar.addControl(btn);
      this.buttons[id] = btn;
    }

    _addSep() {
      const sep = document.createElement("div");
      sep.className = "toolbar-separator";
      this.subToolbar.container?.appendChild(sep);
    }

    // ─── Bridge ────────────────────────────────────────────────────────────────

    registerBridge() {
      window.__issueSidebar = {
        open: null,
        close: null,
        onSubmit: (fd) => this.handleSidebarSubmit(fd),
        onCancel: () => this.handleSidebarCancel(),
        getIssues: () => this.allIssues,
        getRFIs: () => this.allRFIs,
        getStats: () => this.getIssueStatistics(),
        getIssueTypeConfigs: () => ISSUE_TYPE_CONFIGS,
        handleCreateClick: (cfg) => this.handleCreateClick(cfg),
        showAllIssues: () => this.showAllPushPins("issues"),
        showAllRFIs: () => this.showAllPushPins("rfis"),
      };
    }

    unregisterBridge() {
      if (window.__issueSidebar) window.__issueSidebar = null;
    }

    // ─── Sidebar callbacks ─────────────────────────────────────────────────────

    handleSidebarSubmit(formData) {
      if (!formData) return;
      this._pendingFormData = formData;
      this._startPinPlacement(formData);
    }

    handleSidebarCancel() {
      this.isCreatingIssue = false;
      this.currentIssueType = null;
      this._pendingFormData = null;
      this.enableAllButtons();
      try {
        this.pushPinExtension?.endCreateItem?.();
      } catch (_) {}
    }

    _startPinPlacement(formData) {
      if (!this.pushPinExtension?.pushPinManager) {
        this.log("PushPin extension not ready", "error");
        return;
      }
      const cfg = this.currentIssueType;
      this.isCreatingIssue = true;
      this.disableAllButtons();
      this.viewer.addEventListener(
        Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
        this.onGeometryLoaded,
      );
      try {
        this.pushPinExtension.startCreateItem({
          label: formData.label,
          status: formData.status,
          type: cfg.type,
          description: formData.description || "",
          category: formData.category,
          issueType: formData.issueType,
          assignedTo: formData.assignedTo,
          watchers: formData.watchers,
          location: formData.location,
          locationDetails: formData.locationDetails,
          startDate: formData.startDate,
          dueDate: formData.dueDate,
          rootCause: formData.rootCause,
        });
        console.log("[BIM360] 🖱️ Click on the model to place the pin…");
      } catch (err) {
        this.removeViewerListeners();
        this.isCreatingIssue = false;
        this.currentIssueType = null;
        this._pendingFormData = null;
        this.enableAllButtons();
        this.log("Failed to start pin placement: " + err.message, "error");
      }
    }

    // ─── Create flow ───────────────────────────────────────────────────────────

    async handleCreateClick(cfg) {
      if (this.isCreatingIssue) {
        this.log("Already creating an issue", "warning");
        return;
      }
      try {
        this.currentIssueType = cfg;
        await this.ensurePushPinExtension();
        if (window.__issueSidebar?.open) {
          window.__issueSidebar.open({ issueTypeConfig: cfg });
        } else {
          setTimeout(() => {
            window.__issueSidebar?.open
              ? window.__issueSidebar.open({ issueTypeConfig: cfg })
              : this.log("React IssueSidebarBridge not mounted.", "error");
          }, 600);
        }
      } catch (err) {
        this.log("Failed to open sidebar: " + err.message, "error");
        this.isCreatingIssue = false;
        this.currentIssueType = null;
        this.enableAllButtons();
      }
    }

    async ensurePushPinExtension() {
      this.pushPinExtension = this.viewer.getExtension(EXT.PUSH_PIN);
      if (!this.pushPinExtension) {
        this.pushPinExtension = await this.viewer.loadExtension(EXT.PUSH_PIN, {
          hideRfisButton: true,
          hideFieldIssuesButton: true,
        });
      }
      if (this.pushPinExtension?.pushPinManager) {
        this.pushPinExtension.pushPinManager.removeEventListener(
          "pushpin.created",
          this.onPushpinCreated,
        );
        this.pushPinExtension.pushPinManager.removeEventListener(
          "pushpin.loaded",
          this.onPushpinLoaded,
        );
        this.pushPinExtension.pushPinManager.addEventListener(
          "pushpin.created",
          this.onPushpinCreated,
        );
        this.pushPinExtension.pushPinManager.addEventListener(
          "pushpin.loaded",
          this.onPushpinLoaded,
        );
        if (!this.pushpinObserver) this._startMutationObserver();
      }
      return this.pushPinExtension;
    }

    _startMutationObserver() {
      const obs = new MutationObserver(() => {
        if (this.pushpinColorMap.size > 0)
          setTimeout(
            () =>
              this.pushpinColorMap.forEach((d, id) =>
                this.applyColorToPushpin(id, d.color),
              ),
            100,
          );
      });
      obs.observe(this.viewer.container, { childList: true, subtree: true });
      this.pushpinObserver = obs;
    }

    // ─── Pushpin events ────────────────────────────────────────────────────────

    async _initIssuesHydration() {
      try {
        await this.loadIssuesFromBackendFirst();
      } catch (err) {
        console.error("[BIM360] API issue load failed", err);
        this.allIssues = [];
        this.allRFIs = [];
        this.pushpinColorMap = new Map();
      }
      if (this.viewer.model) {
        setTimeout(() => this.restorePushpinsFromStorage(), 1000);
      } else {
        this.viewer.addEventListener(
          Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
          () => setTimeout(() => this.restorePushpinsFromStorage(), 1000),
          { once: true },
        );
      }
    }

    async loadIssuesFromBackendFirst() {
      let docUrn = window.__viewstructDocumentUrn;
      for (let i = 0; i < 40 && !docUrn; i++) {
        await new Promise((r) => setTimeout(r, 150));
        docUrn = window.__viewstructDocumentUrn;
      }
      let urnId = window.__viewstructUserUrnId;
      if (!urnId && docUrn) {
        urnId = await resolveUserUrnId(docUrn);
        if (urnId) window.__viewstructUserUrnId = urnId;
      }
      if (!urnId) {
        this.allIssues = [];
        this.allRFIs = [];
        this.pushpinColorMap = new Map();
        return;
      }
      const rows = await listIssues({ urnId, limit: 300 });
      this.allIssues = [];
      this.allRFIs = [];
      this.pushpinColorMap = new Map();
      for (const row of rows) {
        const local = this._serverRowToLocalIssue(row);
        const color =
          local.statusColor || STATUS_COLORS[local.status] || "#FF9800";
        local.statusColor = color;
        this.pushpinColorMap.set(local.id, {
          color,
          status: local.status,
        });
        if (local.type === "rfis") this.allRFIs.push(local);
        else this.allIssues.push(local);
      }
      console.log(
        `[BIM360] ✅ Loaded ${this.allIssues.length} issues, ${this.allRFIs.length} RFIs from API`,
      );
    }

    _serverRowToLocalIssue(row) {
      const client = row.payload?.clientIssue;
      if (client && typeof client === "object") {
        return { ...client, __serverIssueId: row.id };
      }
      const p = row.payload?.pushpinRestore || {};
      const modelUrn =
        this.viewer?.model?.getData?.()?.urn?.replace(/^urn:/, "") || "";
      const id = String(p.id ?? row.id);
      return {
        id,
        label: row.label,
        status: row.status,
        type: row.issue_type === "rfis" ? "rfis" : "issues",
        subtype: p.subtype || row.payload?.subtype || "general_issue",
        description: row.description || "",
        position: p.position || { x: 0, y: 0, z: 0 },
        objectData: p.objectData || {},
        viewerState: p.viewerState || {},
        seedURN: p.seedURN || modelUrn,
        externalId: p.externalId || "",
        objectId: p.objectId || 0,
        locationIds: p.locationIds || [],
        attributesVersion: p.attributesVersion || 2,
        __serverIssueId: row.id,
        statusColor: STATUS_COLORS[row.status] || "#FF9800",
        createdAt: row.created_at,
      };
    }

    async _persistNewIssueToApi(issue, itemData) {
      if (issue.__serverIssueId) return;
      let urnId = window.__viewstructUserUrnId;
      const docUrn = window.__viewstructDocumentUrn;
      if (!urnId && docUrn) {
        urnId = await resolveUserUrnId(docUrn);
        if (urnId) window.__viewstructUserUrnId = urnId;
      }
      if (!urnId) return;
      try {
        const description =
          this._pendingFormData?.description || issue.description || "";
        const res = await createIssue({
          user_urn_id: urnId,
          issue_type: itemData.type,
          status: itemData.status || this._pendingFormData?.status || "open",
          label: itemData.label,
          description,
          payload: { clientIssue: issue },
        });
        issue.__serverIssueId = res.id;
      } catch (err) {
        console.error("[BIM360] Failed to persist issue to API:", err);
        this.log(
          "Could not save issue to server (local backup only)",
          "warning",
        );
      }
    }

    async restorePushpinsFromStorage() {
      if (!this.allIssues.length && !this.allRFIs.length) return;

      try {
        await this.ensurePushPinExtension();
        const allItems = [...this.allIssues, ...this.allRFIs];

        this._restoringPins = true;
        try {
          for (const issue of allItems) {
            try {
              this.pushPinExtension.pushPinManager.createItem({
                id: issue.id,
                label: issue.label,
                status: issue.status,
                type: issue.type,
                position: issue.position,
                objectData: issue.objectData || {},
                viewerState: issue.viewerState || {},
                seedURN: issue.seedURN || "",
                externalId: issue.externalId || "",
                objectId: issue.objectId || 0,
                locationIds: issue.locationIds || [],
                attributesVersion: issue.attributesVersion || 2,
              });

              const color = issue.statusColor || "#FF9800";
              this.pushpinColorMap.set(issue.id, {
                color,
                status: issue.status,
              });
              setTimeout(() => this.applyColorToPushpin(issue.id, color), 500);
            } catch (e) {
              console.warn(`[BIM360] Failed to restore pin ${issue.id}:`, e);
            }
          }
        } finally {
          this._restoringPins = false;
        }

        console.log(
          `[BIM360] ✅ Restored ${allItems.length} pins from storage`,
        );
      } catch (err) {
        console.error("[BIM360] restorePushpinsFromStorage error:", err);
        this._restoringPins = false;
      }
    }
    async onPushpinCreated(event) {
      console.log("[BIM360] 📍 Pushpin created:", event);
      try {
        this.pushPinExtension?.endCreateItem?.();
        const pin = event.value;
        const itemData = pin?.itemData;
        if (!itemData?.id) return;

        const sameId = (i) => String(i.id) === String(itemData.id);
        const alreadyFromApi = [...this.allIssues, ...this.allRFIs].find(
          (i) => sameId(i) && i.__serverIssueId,
        );

        // Restoring pins via createItem fires pushpin.created — do not POST again or duplicate arrays.
        if (this._restoringPins || alreadyFromApi) {
          const src = alreadyFromApi;
          const color =
            src?.statusColor ||
            STATUS_COLORS[src?.status || itemData.status] ||
            "#FF9800";
          this.pushpinColorMap.set(itemData.id, {
            color,
            status: src?.status || itemData.status,
          });
          [100, 300, 500, 800].forEach((d) =>
            setTimeout(() => this.applyColorToPushpin(itemData.id, color), d),
          );
          return;
        }

        const cfg = this.currentIssueType || {
          type: itemData.type,
          subtype: "general_issue",
          title: "Issue",
          icon: "📍",
          color: "#9E9E9E",
          pinColor: "#9E9E9E",
        };
        const color = STATUS_COLORS[this._pendingFormData?.status] || "#FF9800";

        // Save raw itemData + extra visual info
        const issue = {
          ...itemData,
          statusColor: color,
          visual: {
            color,
            pinColor: cfg.pinColor || color,
            icon: cfg.icon || "📍",
          },
          createdAt: new Date().toISOString(),
        };
        console.log(
          "Issue created with data:-=-=-=-=-=-=-=-=-=-=--==-=-=-=- ",
          issue,
          "info",
        );
        if (itemData.type === "issues") this.allIssues.push(issue);
        else if (itemData.type === "rfis") this.allRFIs.push(issue);

        await this._persistNewIssueToApi(issue, itemData);

        // Apply color to pin
        this.pushpinColorMap.set(itemData.id, {
          color,
          status: itemData.status,
        });
        [100, 300, 500, 800, 1200].forEach((d) =>
          setTimeout(() => this.applyColorToPushpin(itemData.id, color), d),
        );

        this.log(`✅ ${cfg.title} "${itemData.label}" saved`, "success");
      } catch (err) {
        console.error("[BIM360] onPushpinCreated error:", err);
      } finally {
        this.removeViewerListeners();
        this.isCreatingIssue = false;
        this.currentIssueType = null;
        this._pendingFormData = null;
        this.enableAllButtons();
      }
    }
    onPushpinLoaded(event) {
      const pin = event.value;
      if (!pin) return;
      const color = STATUS_COLORS[pin.status] || "#9E9E9E";
      this.pushpinColorMap.set(pin.id, {
        color,
        subtype: pin.subtype,
        status: pin.status,
      });
      [100, 300, 500, 800].forEach((d) =>
        setTimeout(() => this.applyColorToPushpin(pin.id, color), d),
      );
      const arr =
        pin.type === "issues"
          ? this.allIssues
          : pin.type === "rfis"
            ? this.allRFIs
            : null;
      const existing = arr?.find((i) => String(i.id) === String(pin.id));
      if (arr && !existing) {
        arr.push(
          this._buildIssue(pin, null, { type: pin.type, subtype: pin.subtype }),
        );
        this.scheduleSyncIssuesToApi();
      } else if (existing?.__serverIssueId) {
        // Pin already tracked from API — do not append duplicate row for PATCH-only path
      }
    }

    onGeometryLoaded() {
      setTimeout(
        () =>
          this.pushpinColorMap.forEach((d, id) =>
            this.applyColorToPushpin(id, d.color),
          ),
        500,
      );
    }

    // ─── Color management ──────────────────────────────────────────────────────

    startColorUpdates() {
      this.colorUpdateInterval = setInterval(
        () => this.continuouslyApplyColors(),
        500,
      );
    }
    stopColorUpdates() {
      if (this.colorUpdateInterval) {
        clearInterval(this.colorUpdateInterval);
        this.colorUpdateInterval = null;
      }
    }
    continuouslyApplyColors() {
      this.pushpinColorMap.forEach((d, id) =>
        this.applyColorToPushpin(id, d.color, true),
      );
    }

    applyColorToPushpin(id, color, isContinuous = false) {
      if (!id || !color) return;
      try {
        document.querySelectorAll("svg").forEach((svg) => {
          if (!svg.querySelector("text")) return;
          const match =
            svg.getAttribute("data-id") === id ||
            svg.getAttribute("id")?.includes(id) ||
            this._svgMatchesId(svg, id);
          if (!match) return;
          svg.querySelectorAll("ellipse, circle").forEach((el) => {
            el.setAttribute("fill", color);
            el.style.fill = color;
          });
          svg
            .querySelectorAll('line, path[class*="stem"], path[class*="Stem"]')
            .forEach((el) => {
              el.setAttribute("stroke", color);
              el.setAttribute("fill", color);
              el.style.stroke = color;
              el.style.fill = color;
            });
        });
        if (this.pushPinExtension?.pushPinManager?.itemList) {
          this.pushPinExtension.pushPinManager.itemList.forEach((item) => {
            if (item.id === id && item.itemData) {
              item.itemData.pushpinAttributes =
                item.itemData.pushpinAttributes || {};
              item.itemData.pushpinAttributes.color = color;
              item.itemData.color = color;
            }
          });
        }
      } catch (e) {
        if (!isContinuous) console.error("applyColorToPushpin:", e);
      }
    }

    _svgMatchesId(svg, id) {
      for (const t of svg.querySelectorAll("text")) {
        if (
          [...this.allIssues, ...this.allRFIs].find(
            (i) =>
              i.id === id &&
              (i.label || i.title) === (t.textContent || t.innerText),
          )
        )
          return true;
      }
      return false;
    }

    // ─── Show all ──────────────────────────────────────────────────────────────

    async showAllPushPins(type) {
      try {
        await this.ensurePushPinExtension();
        const items = type === "issues" ? this.allIssues : this.allRFIs;
        items.forEach((item) =>
          this.pushPinExtension.pushPinManager.showItem?.(item.id),
        );
        setTimeout(() => {
          items.forEach((item) => {
            const color = STATUS_COLORS[item.status] || "#9E9E9E";
            this.applyColorToPushpin(item.id, color);
            this.pushpinColorMap.set(item.id, { color, status: item.status });
          });
        }, 500);
      } catch (err) {
        console.error("showAllPushPins:", err);
      }
    }

    // ─── Helpers ───────────────────────────────────────────────────────────────

    _inferConfig(pinType, form) {
      if (pinType === "rfis") return ISSUE_TYPE_CONFIGS.RFI;
      if (!form)
        return {
          type: pinType,
          subtype: "general_issue",
          title: "Issue",
          icon: "📍",
          color: "#9E9E9E",
          pinColor: "#9E9E9E",
        };
      if (form.subtype?.includes("quality")) return ISSUE_TYPE_CONFIGS.QUALITY;
      if (form.subtype?.includes("safety")) return ISSUE_TYPE_CONFIGS.SAFETY;
      if (form.subtype?.includes("design")) return ISSUE_TYPE_CONFIGS.DESIGN;
      return ISSUE_TYPE_CONFIGS.QUALITY;
    }

    _buildIssue(pin, form, cfg) {
      const color = STATUS_COLORS[form?.status || pin.status] || "#9E9E9E";
      const pos = this._pos(pin);
      return {
        id: pin.id,
        type: pin.type || cfg.type,
        subtype: cfg.subtype,
        title: form?.label || pin.label || "",
        description: form?.description || pin.description || "",
        status: form?.status || pin.status,
        statusColor: color,
        category: form?.category || pin.category,
        issueType: form?.issueType || pin.issueType,
        assignedTo: form?.assignedTo || null,
        watchers: form?.watchers || null,
        createdBy: "User",
        location: form?.location || null,
        locationDetails: form?.locationDetails || null,
        position3D: { ...pos, units: "model units" },
        camera: this._camera(),
        viewerState: this._viewerState(),
        startDate: form?.startDate || null,
        dueDate: form?.dueDate || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        rootCause: form?.rootCause || null,
        model: {
          urn: this.viewer.model?.getData()?.urn || null,
          guid: this.viewer.model?.getData()?.guid || null,
          name: this.viewer.model?.getData()?.name || null,
        },
        visual: {
          color,
          pinColor: cfg.pinColor || color,
          icon: cfg.icon || "📍",
        },
        metadata: { version: "2.4.0", extensionName: "BIM360IssueExtension" },
      };
    }

    _pos(pin) {
      if (pin.position)
        return {
          x: pin.position.x || 0,
          y: pin.position.y || 0,
          z: pin.position.z || 0,
        };
      if (pin.objectData?.position)
        return {
          x: pin.objectData.position.x || 0,
          y: pin.objectData.position.y || 0,
          z: pin.objectData.position.z || 0,
        };
      if (pin.itemData?.position)
        return {
          x: pin.itemData.position.x || 0,
          y: pin.itemData.position.y || 0,
          z: pin.itemData.position.z || 0,
        };
      return { x: 0, y: 0, z: 0 };
    }

    _viewerState() {
      try {
        const s = this.viewer.getState();
        return {
          viewport: s.viewport || null,
          cutplanes: s.cutplanes || [],
          objectSet: s.objectSet || [],
          explodeScale: s.explodeScale || 0,
          renderOptions: s.renderOptions || {},
          selection: this.viewer.getSelection(),
          isolatedNodes: this.viewer.getIsolatedNodes(),
          hiddenNodes: this.viewer.getHiddenNodes(),
        };
      } catch {
        return null;
      }
    }

    _camera() {
      try {
        const p = this.viewer.navigation.getPosition();
        const t = this.viewer.navigation.getTarget();
        const v = this.viewer.navigation.getPivotPoint();
        const c = this.viewer.navigation.getCamera();
        return {
          position: { x: p.x, y: p.y, z: p.z },
          target: { x: t.x, y: t.y, z: t.z },
          pivot: v ? { x: v.x, y: v.y, z: v.z } : null,
          fov: c.fov || null,
          isPerspective: c.isPerspective || false,
          distance: this.viewer.navigation.getEyeVector().length(),
        };
      } catch {
        return null;
      }
    }

    getIssueStatistics() {
      const stats = {
        total: this.allIssues.length + this.allRFIs.length,
        issues: this.allIssues.length,
        rfis: this.allRFIs.length,
        byStatus: {},
        byCategory: {},
      };
      [...this.allIssues, ...this.allRFIs].forEach((i) => {
        stats.byStatus[i.status] = (stats.byStatus[i.status] || 0) + 1;
        if (i.category)
          stats.byCategory[i.category] =
            (stats.byCategory[i.category] || 0) + 1;
      });
      return stats;
    }

    // ─── API sync (no localStorage) ───────────────────────────────────────────

    startAutoBackup() {
      this.backupInterval = setInterval(
        () => void this.syncIssuesToApi(),
        2 * 60 * 1000,
      );
    }
    stopAutoBackup() {
      if (this.backupInterval) {
        clearInterval(this.backupInterval);
        this.backupInterval = null;
      }
      if (this._syncTimer) {
        clearTimeout(this._syncTimer);
        this._syncTimer = null;
      }
    }

    scheduleSyncIssuesToApi() {
      if (this._syncTimer) clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => {
        this._syncTimer = null;
        void this.syncIssuesToApi();
      }, 1500);
    }

    _cloneForPayload(obj) {
      try {
        return JSON.parse(JSON.stringify(obj));
      } catch {
        return {};
      }
    }

    async syncIssuesToApi() {
      for (const issue of [...this.allIssues, ...this.allRFIs]) {
        const sid = issue.__serverIssueId;
        if (!sid) continue;
        try {
          const issueType = issue.type === "rfis" ? "rfis" : "issues";
          const clone = this._cloneForPayload(issue);
          await updateIssue(sid, {
            issue_type: issueType,
            status: issue.status || "open",
            label: issue.label || issue.title || "Issue",
            description: issue.description || "",
            payload: { clientIssue: clone },
          });
        } catch (e) {
          console.warn("[BIM360] sync issue failed", sid, e);
        }
      }
    }

    // ─── Toolbar styles ────────────────────────────────────────────────────────

    // injectToolbarStyles() {
    //   const id = "bim360-toolbar-styles";
    //   if (document.getElementById(id)) return;
    //   const s = document.createElement("style");
    //   s.id = id;
    //   s.textContent = `
    //     .adsk-button.createQualityIssue{background:linear-gradient(135deg,#FF9800,#F57C00)!important;border:2px solid #E65100!important;position:relative}
    //   .adsk-button.createQualityIssue::before{content:"";background-image:url(${pushImg});background-size:20px 20px;background-repeat:no-repeat;background-position:center;width:20px;height:20px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    //     .adsk-button.createSafetyIssue{background:linear-gradient(135deg,#F44336,#D32F2F)!important;border:2px solid #B71C1C!important;position:relative}
    //     .adsk-button.createSafetyIssue::before{content:"🛡️";font-size:18px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    //     .adsk-button.createDesignIssue{background:linear-gradient(135deg,#2196F3,#1976D2)!important;border:2px solid #0D47A1!important;position:relative}
    //     .adsk-button.createDesignIssue::before{content:"📐";font-size:18px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    //     .adsk-button.createRFI{background:linear-gradient(135deg,#4CAF50,#388E3C)!important;border:2px solid #1B5E20!important;position:relative}
    //     .adsk-button.createRFI::before{content:"❓";font-size:18px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    //     .adsk-button.showAllIssues{background:linear-gradient(135deg,#9C27B0,#7B1FA2)!important;border:2px solid #4A148C!important;position:relative}
    //     .adsk-button.showAllIssues::before{content:"📋";font-size:18px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    //     .adsk-button.showAllRFIs{background:linear-gradient(135deg,#009688,#00796B)!important;border:2px solid #004D40!important;position:relative}
    //     .adsk-button.showAllRFIs::before{content:"📝";font-size:18px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}
    //     .adsk-button.createQualityIssue,.adsk-button.createSafetyIssue,.adsk-button.createDesignIssue,
    //     .adsk-button.createRFI,.adsk-button.showAllIssues,.adsk-button.showAllRFIs{
    //       width:40px!important;height:40px!important;border-radius:8px!important;
    //       transition:all 0.3s ease!important;cursor:pointer!important}
    //     .toolbar-separator{display:inline-block;width:1px;height:32px;background:rgba(255,255,255,.2);margin:0 8px;vertical-align:middle}
    //   `;
    //   document.head.appendChild(s);
    // }

    injectToolbarStyles() {
      const id = "bim360-toolbar-styles";
      if (document.getElementById(id)) return;
      const s = document.createElement("style");
      s.id = id;
      s.textContent = `
    .adsk-button.createQualityIssue,
    .adsk-button.createSafetyIssue,
    .adsk-button.createDesignIssue,
    .adsk-button.createRFI,
    .adsk-button.showAllIssues,
    .adsk-button.showAllRFIs{
      width:34px!important;
      height:34px!important;
      border-radius:4px!important;
      background:transparent!important;
      border:none!important;
      position:relative;
      transition:background 0.12s ease!important;
      cursor:pointer!important;
      box-shadow:none!important;
    }
    .adsk-button.createQualityIssue:hover,
    .adsk-button.createSafetyIssue:hover,
    .adsk-button.createDesignIssue:hover,
    .adsk-button.createRFI:hover,
    .adsk-button.showAllIssues:hover,
    .adsk-button.showAllRFIs:hover{
      background:rgba(255,255,255,0.14)!important;
    }
    .adsk-button.createQualityIssue:active,
    .adsk-button.createSafetyIssue:active,
    .adsk-button.createDesignIssue:active,
    .adsk-button.createRFI:active,
    .adsk-button.showAllIssues:active,
    .adsk-button.showAllRFIs:active{
      background:rgba(255,255,255,0.22)!important;
    }
 .adsk-button.createIssueCombo{
       content:"";background-image:url(${pushImg});
      background-size:24px;background-repeat:no-repeat;background-position:center;
 }
    .adsk-button.createQualityIssue::before{
      content:"";background-image:url(${pushImg});
      filter:brightness(0) invert(1);
      background-size:17px 17px;background-repeat:no-repeat;background-position:center;
      width:17px;height:17px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
    }
    .adsk-button.createSafetyIssue::before{
      content:"";background-image:url(${ICONS.safety});
      background-size:17px 17px;background-repeat:no-repeat;background-position:center;
      width:17px;height:17px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
    }
    .adsk-button.createDesignIssue::before{
      content:"";background-image:url(${ICONS.design});
      background-size:17px 17px;background-repeat:no-repeat;background-position:center;
      width:17px;height:17px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
    }
    .adsk-button.createRFI::before{
      content:"";background-image:url(${ICONS.rfi});
      background-size:17px 17px;background-repeat:no-repeat;background-position:center;
      width:17px;height:17px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
    }
    .adsk-button.showAllIssues::before{
      content:"";background-image:url(${ICONS.showIssues});
      background-size:17px 17px;background-repeat:no-repeat;background-position:center;
      width:17px;height:17px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
    }
    .adsk-button.showAllRFIs::before{
      content:"";background-image:url(${ICONS.showRfis});
      background-size:17px 17px;background-repeat:no-repeat;background-position:center;
      width:17px;height:17px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
    }


  `;
      document.head.appendChild(s);
    }
    removeStyles() {
      document.getElementById("bim360-toolbar-styles")?.remove();
    }

    disableAllButtons() {
      Object.values(this.buttons).forEach((b) =>
        b?.setState(Autodesk.Viewing.UI.Button.State.DISABLED),
      );
    }
    enableAllButtons() {
      Object.values(this.buttons).forEach((b) =>
        b?.setState(Autodesk.Viewing.UI.Button.State.INACTIVE),
      );
    }

    removeViewerListeners() {
      if (this.pushPinExtension?.pushPinManager) {
        this.pushPinExtension.pushPinManager.removeEventListener(
          "pushpin.created",
          this.onPushpinCreated,
        );
        this.pushPinExtension.pushPinManager.removeEventListener(
          "pushpin.loaded",
          this.onPushpinLoaded,
        );
      }
      this.viewer.removeEventListener(
        Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
        this.onGeometryLoaded,
      );
    }

    log(msg, type = "info") {
      const c = {
        success: "green",
        error: "red",
        warning: "orange",
        info: "dodgerblue",
      };
      console.log(
        `%c[BIM360] ${msg}`,
        `color:${c[type] || c.info};font-weight:bold`,
      );
    }

    getAllIssues() {
      return this.allIssues;
    }
    getAllRFIs() {
      return this.allRFIs;
    }
    getIssueById(id) {
      return (
        [...this.allIssues, ...this.allRFIs].find((i) => i.id === id) || null
      );
    }
  }

  window.Autodesk.Viewing.theExtensionManager.registerExtension(
    EXT.BIM360_ISSUE,
    BIM360IssueExtension,
  );
  return { id: "BIM360IssueExtension", options: {} };
};
